import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Button, Text } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import { Palette, radius, spacing, useThemePalette } from '../theme';
import ModalSheet from './ModalSheet';

interface DatePickerProps {
  isVisible: boolean;
  onCancel: () => void;
  onConfirm: (date: Date) => void;
  initialDate?: Date;
  minimumDate?: Date;
  maximumDate?: Date;
}

const WEEK_LENGTH = 7;
const TOTAL_CELLS = 42;

const stripTime = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const DatePicker: React.FC<DatePickerProps> = ({
  isVisible,
  onCancel,
  onConfirm,
  initialDate = new Date(),
  minimumDate = new Date(2023, 0, 1),
  maximumDate = new Date(),
}) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [selected, setSelected] = useState(initialDate);
  const [yearMode, setYearMode] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    setViewMonth(initialDate.getMonth());
    setViewYear(initialDate.getFullYear());
    setSelected(initialDate);
    setYearMode(false);
    // Re-sync only when the picker opens; initialDate is recreated each
    // render in the parent and would otherwise reset state on every keypress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const months = t('datepicker.months', { returnObjects: true }) as string[];
  const weekdays = t('datepicker.weekdays', { returnObjects: true }) as string[];

  const minTs = stripTime(minimumDate);
  const maxTs = stripTime(maximumDate);
  const minMonthTs = new Date(
    minimumDate.getFullYear(),
    minimumDate.getMonth(),
    1
  ).getTime();
  const maxMonthTs = new Date(
    maximumDate.getFullYear(),
    maximumDate.getMonth(),
    1
  ).getTime();
  const viewStartTs = new Date(viewYear, viewMonth, 1).getTime();

  const goPrev = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const goNext = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(viewYear, viewMonth, d));
  }
  while (cells.length < TOTAL_CELLS) cells.push(null);

  const prevDisabled = viewStartTs <= minMonthTs;
  const nextDisabled = viewStartTs >= maxMonthTs;

  const years: number[] = [];
  for (let y = maximumDate.getFullYear(); y >= minimumDate.getFullYear(); y--) {
    years.push(y);
  }

  const selectedTs = stripTime(selected);

  return (
    <ModalSheet
      visible={isVisible}
      onDismiss={onCancel}
      cardStyle={styles.card}
    >
      <View style={styles.body}>
          <View style={styles.headerRow}>
            <TouchableOpacity
              onPress={goPrev}
              disabled={prevDisabled || yearMode}
              style={[
                styles.navBtn,
                (prevDisabled || yearMode) && styles.navBtnDisabled,
              ]}
              hitSlop={8}
              accessibilityLabel={t('datepicker.previousMonth')}
            >
              <MaterialCommunityIcons
                name="chevron-left"
                size={24}
                color={
                  prevDisabled || yearMode
                    ? palette.textMuted
                    : palette.textPrimary
                }
              />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => setYearMode(!yearMode)}
              style={styles.headerLabel}
              activeOpacity={0.7}
            >
              <Text style={styles.headerLabelText}>
                {months[viewMonth]} {viewYear}
              </Text>
              <MaterialCommunityIcons
                name={yearMode ? 'menu-up' : 'menu-down'}
                size={20}
                color={palette.textPrimary}
              />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={goNext}
              disabled={nextDisabled || yearMode}
              style={[
                styles.navBtn,
                (nextDisabled || yearMode) && styles.navBtnDisabled,
              ]}
              hitSlop={8}
              accessibilityLabel={t('datepicker.nextMonth')}
            >
              <MaterialCommunityIcons
                name="chevron-right"
                size={24}
                color={
                  nextDisabled || yearMode
                    ? palette.textMuted
                    : palette.textPrimary
                }
              />
            </TouchableOpacity>
          </View>

          {yearMode ? (
            <ScrollView
              style={styles.yearScroll}
              contentContainerStyle={styles.yearGrid}
            >
              {years.map((y) => {
                const isCurrent = y === viewYear;
                return (
                  <TouchableOpacity
                    key={y}
                    onPress={() => {
                      setViewYear(y);
                      setYearMode(false);
                    }}
                    style={[styles.yearItem, isCurrent && styles.yearItemSelected]}
                  >
                    <Text
                      style={[styles.yearText, isCurrent && styles.yearTextSelected]}
                    >
                      {y}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          ) : (
            <>
              <View style={styles.weekRow}>
                {weekdays.map((d, i) => (
                  <Text key={`${d}-${i}`} style={styles.weekdayText}>
                    {d}
                  </Text>
                ))}
              </View>
              <View style={styles.grid}>
                {cells.map((cell, i) => {
                  if (!cell) {
                    return <View key={`empty-${i}`} style={styles.cell} />;
                  }
                  const ts = stripTime(cell);
                  const disabled = ts < minTs || ts > maxTs;
                  const isSelected = ts === selectedTs;
                  return (
                    <TouchableOpacity
                      key={ts}
                      style={styles.cell}
                      onPress={() => !disabled && setSelected(cell)}
                      disabled={disabled}
                      activeOpacity={0.7}
                    >
                      <View
                        style={[
                          styles.cellInner,
                          isSelected && styles.cellInnerSelected,
                        ]}
                      >
                        <Text
                          style={[
                            styles.cellText,
                            disabled && styles.cellTextDisabled,
                            isSelected && styles.cellTextSelected,
                          ]}
                        >
                          {cell.getDate()}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </>
          )}

          <View style={styles.footer}>
            <Button
              mode="text"
              onPress={onCancel}
              textColor={palette.textPrimary}
              style={styles.footerBtn}
            >
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={() => onConfirm(selected)}
              buttonColor={palette.primary}
              textColor="#0F1115"
              style={styles.footerBtn}
            >
              {t('common.ok')}
            </Button>
          </View>
        </View>
    </ModalSheet>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    card: {
      maxWidth: 420,
      backgroundColor: p.surface,
      borderRadius: radius.lg,
    },
    body: { padding: spacing.lg },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing.md,
    },
    navBtn: {
      width: 36,
      height: 36,
      borderRadius: radius.pill,
      alignItems: 'center',
      justifyContent: 'center',
    },
    navBtnDisabled: { opacity: 0.4 },
    headerLabel: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.xs,
    },
    headerLabelText: {
      fontSize: 16,
      fontWeight: '700',
      color: p.textPrimary,
    },
    weekRow: {
      flexDirection: 'row',
      marginBottom: spacing.xs,
    },
    weekdayText: {
      flex: 1,
      textAlign: 'center',
      fontSize: 12,
      fontWeight: '600',
      color: p.textMuted,
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    cell: {
      width: `${100 / WEEK_LENGTH}%`,
      aspectRatio: 1,
      padding: 2,
    },
    cellInner: {
      flex: 1,
      borderRadius: radius.pill,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cellInnerSelected: {
      backgroundColor: p.primary,
    },
    cellText: {
      fontSize: 14,
      color: p.textPrimary,
    },
    cellTextDisabled: {
      color: p.textMuted,
      opacity: 0.4,
    },
    cellTextSelected: {
      color: '#0F1115',
      fontWeight: '700',
    },
    yearScroll: {
      maxHeight: 240,
    },
    yearGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    yearItem: {
      width: '23%',
      paddingVertical: spacing.sm,
      borderRadius: radius.md,
      alignItems: 'center',
      marginBottom: spacing.sm,
      backgroundColor: p.surfaceMuted,
    },
    yearItemSelected: {
      backgroundColor: p.primary,
    },
    yearText: {
      fontSize: 14,
      color: p.textPrimary,
    },
    yearTextSelected: {
      color: '#0F1115',
      fontWeight: '700',
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: spacing.sm,
      marginTop: spacing.md,
    },
    footerBtn: { borderRadius: radius.pill },
  });

export default DatePicker;
